import React from 'react';
import { Row, Select, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Colors from '../../constants/Colors';
import DisplayText from '../../common/text/DisplayText';
import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import CheckRows from '../CheckRows';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  filterSettingsRows,
  isSettingEnabled,
  getTimeCardRows,
} from './timeTrackingConstants';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import Editable from '../../common/editable/Editable';
import { toTitleCase } from '../../helpers/helpers';

const { Option } = Select;
const defaultText = 'By signing below I agree that this time card is accurate and that I was not injured during this duration';

export default function TimeCards({
  settings,
  saveCompanySettings,
  users,
  positionNames = [],
  firstApprover,
  secondApprover,
  approverPositions,
}) {
  const { t } = useTranslation();
  const validApproverPositions = positionNames.filter(({ name }) => approverPositions.has(name));
  const validApproverUsers = users.filter(({ position }) => approverPositions.has(position));
  const {
    enableTimecardSubmission,
  } = settings;

  const timeCardRows = getTimeCardRows(t);
  const enableSubmissionRow = timeCardRows[0];
  const filteredTimeCardSettingRows = filterSettingsRows(timeCardRows.slice(1, 5), settings);
  const showSubmissionDisclaimer = isSettingEnabled(settings, 'submissionDisclaimer') && enableTimecardSubmission;

  const handleSubmissionSettingsToggle = () => {
    const isCurrentlyEnabled = enableTimecardSubmission !== false;
    const action = isCurrentlyEnabled ? 'disable' : 'enable';

    const baseText = `Are you sure you want to ${action} time card submissions and approvals?`;
    const disableWarningText = 'All existing submitted, approved, or rejected time cards will be reset '
      + 'to an unsubmitted status and any pending approvals will be deleted.';
    const content = isCurrentlyEnabled ? (
      <p>
        {baseText}
        <br />
        <br />
        <strong>{disableWarningText}</strong>
      </p>
    ) : (
      <p>{baseText}</p>
    );

    CustomConfirmModal({
      title: `${toTitleCase(action)} Time Card Submission/Approval`,
      content,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => saveCompanySettings({
        enableTimecardSubmission: !isCurrentlyEnabled,
      }),
    });
  };

  return (
    <CompanySettingsCard title="Time Cards">
      <CompanyEditRow
        title={enableSubmissionRow.title}
        helpText={enableSubmissionRow.helpText}
      >
        <OnTraccrSwitch
          onChange={handleSubmissionSettingsToggle}
          style={{ marginRight: 40 }}
          value={enableTimecardSubmission}
        />
      </CompanyEditRow>
      <CheckRows
        data={filteredTimeCardSettingRows}
        isChecked={(item) => settings[item.key]}
        onChange={(item) => saveCompanySettings({
          [item.key]: !settings[item.key],
        })}
      />

      {enableTimecardSubmission && (
      <Row justify="left" style={{ width: '100%', margin: 10 }}>
        <DisplayText title="Companywide Time Card Approver(s)" style={{ fontFamily: 'roboto-medium', marginBottom: 0 }} />
        <DisplayText
          title="Identify which users or role types will be able to approve submitted time cards for users who do not already have an assigned  approver within the project settings."
          style={{
            fontFamily: 'roboto-regular', marginBottom: 0, color: Colors.ONTRACCR_OPACITY_GRAY,
          }}
        />
        <DisplayText title="First Approver" style={{ fontFamily: 'roboto-regular', marginBottom: 0 }} />
        <Select
          style={{
            width: 300,
          }}
          defaultValue={firstApprover}
          placeholder={firstApprover || 'Any Admin'}
          onChange={(item) => {
            if (!item) {
              const adminObject = validApproverPositions.find(({ name }) => name === 'Admin');
              item = adminObject?.id || null;
            }
            saveCompanySettings({
              firstApprover: item,
            });
          }}
          allowClear
          showSearch
          filterOption={(input, option) => {
            return typeof (option.props.children) === 'string' && option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        >
          {validApproverPositions.map((p) => {
            return (<Option key={p.id+'2'} value={p.id}>Any {p.name}</Option>);
          })}
          {validApproverUsers.map((u) => {
            return (<Option key={u.id+'1'} value={u.id}>{u.name}</Option>)
          })}
        </Select>
        <DisplayText title="Second Approver" style={{ fontFamily: 'roboto-regular', marginBottom: 0 }} />
        <Select
          style={{
            width: 300,
          }}
          defaultValue={secondApprover}
          placeholder={secondApprover || 'Select the second default approver/group'}
          onChange={(item) => {
            saveCompanySettings({
              secondApprover: item || null,
            });
          }}
          allowClear
          showSearch
          filterOption={(input, option) => {
            return typeof (option.props.children) === 'string' && option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        >
          <Option key="no1apr" value={null}>None</Option>
          {validApproverPositions.map((p) => {
            return (<Option key={p.id+'22'} value={p.id}>Any {p.name}</Option>)
          })}
          {validApproverUsers.map((u) => {
            return (<Option key={u.id+'12'} value={u.id}>{u.name}</Option>)
          })}
        </Select>
        <Divider 
          style={{
            margin: 0,
            marginTop: 10,
            backgroundColor: Colors.ONTRACCR_OPACITY_GRAY,
            maxWidth: 440,
            minWidth: 440,
          }}
        />
      </Row>
      )}
      <Row hidden={!showSubmissionDisclaimer} justify="center" style={{ width: '100%', margin: 10, marginTop: 20 }}>
        <DisplayText title="Signature Disclaimer Text" style={{ fontFamily: 'roboto-medium', marginBottom: 0 }} />
        <DisplayText
          title="This disclaimer will be displayed to users when they submit their time cards."
          style={{
            fontFamily: 'roboto-regular', marginBottom: 0, color: Colors.ONTRACCR_OPACITY_GRAY,
          }}
        />
        <Editable
          key="disclaimer"
          name="disclaimer"
          defaultValue={settings.disclaimer || defaultText}
          style={{ marginBottom: 0, textAlign: 'center' }}
          onChange={(type, value) => saveCompanySettings({
            disclaimer: value,
          })}
          inputNode={({
            setReturnValue,
            setFocus,
            setHover,
            defaultValue,
          }) =>
              <OnTraccrTextInput
                textarea
                style={{ textAlign: 'center', width: 375 }}
                defaultValue={defaultValue}
                placeholder="Enter Disclaimer Text"
                /*
                  onBlur needs a timeout due to a race condition.
                  If user clicks checkmark, onBlur will fire, as will onClick for
                  the check. These will race, causing the input to not be saved
                  if onBlur executes first. We delay by 100ms to allow
                  onClick check to execute first.
                */
                onBlur={() => setTimeout(() => setFocus(false), 100)}
                onMouseLeave={() => setHover(false)}
                onChange={(e) => setReturnValue({ name: 'disclaimer', value: e.target.value })}
                onClick={() => setFocus(true)}
              />}
        />
      </Row>
    </CompanySettingsCard>
  );
}

TimeCards.propTypes = {
  settings: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  saveCompanySettings: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  positionNames: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  firstApprover: PropTypes.string.isRequired,
  secondApprover: PropTypes.string.isRequired,
  approverPositions: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};
